import React, { FC, ReactElement } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Table } from "antd";
import { AnyAction, AsyncThunk } from "@reduxjs/toolkit";

import {
  HeaderResponse,
  OrderResponse,
  UserOrdersRequest,
} from "../../types/types";
import { ACCOUNT_USER_ORDERS } from "../../constants/routeConstants";
import { selectTotalElements } from "../../redux-toolkit/orders/orders-selector";
import { AppDispatch } from "../../store";

type PropsType = {
  orders: Array<OrderResponse>;
  loading: boolean;
  fetchOrders:
    | AsyncThunk<HeaderResponse<OrderResponse>, number, {}>
    | AsyncThunk<HeaderResponse<OrderResponse>, UserOrdersRequest, {}>;
  userEmail?: string;
};

const OrdersTable: FC<PropsType> = ({
  orders,
  loading,
  fetchOrders,
  userEmail,
}): ReactElement => {
  const totalElements = useSelector(selectTotalElements);
  const dispatch = useDispatch<AppDispatch>();

  const handleTableChange = (pagination: any) => {
    const page = pagination.current - 1;

    if (userEmail) {
      dispatch(
        (fetchOrders as AsyncThunk<
          HeaderResponse<OrderResponse>,
          UserOrdersRequest,
          {}
        >)({ email: userEmail, page })
      );
    } else {
      dispatch(
        (fetchOrders as AsyncThunk<
          HeaderResponse<OrderResponse>,
          number,
          {}
        >)(page)
      );
    }
  };

  const columns = [
    {
      title: "Order №",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a: OrderResponse, b: OrderResponse) =>
        a.date.localeCompare(b.date),
    },
    {
      title: "Customer",
      dataIndex: "firstName",
      key: "firstName",
      render: (_: any, order: OrderResponse) =>
        `${order.firstName ?? ""} ${order.lastName ?? ""}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Sum, $",
      dataIndex: "totalPrice",
      key: "totalPrice",
      sorter: (a: OrderResponse, b: OrderResponse) =>
        a.totalPrice - b.totalPrice,
    },
    {
      title: "Actions",
      key: "operations",
      render: (_: any, order: OrderResponse) => (
        <Link
          to={`${ACCOUNT_USER_ORDERS}/${order.id}`}
          style={{
            color: "#2874f0",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          Show more
        </Link>
      ),
    },
  ];

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <>
      {isMobile ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginTop: "8px",
          }}
        >
          {orders.map((order) => (
            <Link
              key={order.id}
              to={`${ACCOUNT_USER_ORDERS}/${order.id}`}
              style={{
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <div
                className="order-card"
                style={{
                  border: "1px solid #e8e8e8",
                  borderRadius: "10px",
                  padding: "10px 12px",
                  background: "#fff",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 500,
                    color: "#333",
                  }}
                >
                  <span>Order #{order.id}</span>
                  <span
                    style={{
                      color: "#777",
                      fontSize: "13px",
                    }}
                  >
                    {order.date}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: "4px",
                    color: "#2874f0",
                    fontWeight: 500,
                    fontSize: "13.5px",
                    textAlign: "right",
                  }}
                >
                  Show More →
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ minWidth: "650px" }}>
            <Table
              sticky
              rowKey="id"
              columns={columns}
              dataSource={orders}
              loading={loading}
              pagination={{
                total: totalElements,
                position: ["bottomCenter"],
                pageSize: 10,
                showSizeChanger: false,
              }}
              onChange={handleTableChange}
              scroll={{ x: 700 }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default OrdersTable;
